import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import {
  faqs,
  howItWorks,
  purchaseSeatsLanguages,
  purchaseGiftCardLanguages,
} from "./helpers";
import {
  DefaultRegistrationTerms,
  DefaultCheckoutTerms,
} from "../../default_terms_and_conditions";

import type { TeamConfig } from "../types";

import SeatMap3dPreview from "public/images/winnipegjets_flex/SSbackgroundimage.jpg";
import AuthBackgroundBanner from "public/images/winnipegjets_flex/SSbackgroundimage.jpg";
import FindGamesBanner from "public/images/winnipegjets_flex/2526JETS002-02_Game-Packs_Website-Assets_1486x148_v1.jpg";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL_2024_v3/jets-assets/Primary%20Logo/NHL_Jets_Primary.png";

const config: TeamConfig = {
  teamSchema: "winnipegjets_flex",
  browserTitle: "Winnipeg Jets Game Packs",
  packages: false,
  teamName: "Winnipeg Jets",

  packs: {
    enabled: true,
    disablePackSelectionLossModal: true,
    dontOpenPackModal: true,
    list: [
      {
        id: 1,
        amount: 4,
        title: "4 Game Pack",
        description: "Choose a total of 4 games across four tiers!",
        offerMoreMessage: "Would you like to add 2 more games?",
        buckets: [
          {
            id: 1,
            title: "Tier 1",
            description: "Pick 1 Game",
            max: 1,
            min: 1,
          },
          {
            id: 2,
            title: "Tier 2",
            description: "Pick 1 Game",
            max: 1,
            min: 1,
          },
          {
            id: 3,
            title: "Tier 3",
            description: "Pick 1 Game",
            max: 1,
            min: 1,
          },
          {
            id: 4,
            title: "Tier 4",
            description: "Pick 1 Game",
            max: 1,
            min: 1,
          },
        ],
        updatedAccountGroup: 394,
        successMessage:
          "Congrats! You've selected your 4 games for your 4 Game Pack.",
        dynamicAccountGroup: [
          {
            originalAccountGroupIds: ["0", "1"],
            updatedAccountGroupId: ["0"],
            priority: 2,
          },
          {
            originalAccountGroupIds: ["394", "396"],
            updatedAccountGroupId: ["394"],
            priority: 1,
          },
        ],
      },
      {
        id: 2,
        amount: 6,
        title: "6 Game Pack",
        description: "Choose a total of 6 games across four tiers!",
        offerMoreMessage: "Would you like to add 2 more games?",
        defaultSelected: true,
        buckets: [
          {
            id: 1,
            title: "Tier 1",
            description: "Pick 2 Games",
            max: 2,
            min: 2,
          },
          {
            id: 2,
            title: "Tier 2",
            description: "Pick 2 Games",
            max: 2,
            min: 2,
          },
          {
            id: 3,
            title: "Tier 3",
            description: "Pick 1 Game",
            max: 1,
            min: 1,
          },
          {
            id: 4,
            title: "Tier 4",
            description: "Pick 1 Game",
            max: 1,
            min: 1,
          },
        ],
        updatedAccountGroup: 396,
        successMessage:
          "Congrats! You've selected your 6 games for your 6 Game Pack.",
        dynamicAccountGroup: [
          {
            originalAccountGroupIds: ["0", "1"],
            updatedAccountGroupId: ["1"],
            priority: 2,
          },
          {
            originalAccountGroupIds: ["394", "396"],
            updatedAccountGroupId: ["396"],
            priority: 1,
          },
        ],
      },
    ],
  },

  manageCard: true,

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.giftCard,
    hideRedeemItem: true,
    hideRecipient: true,
  },

  // disabled routes
  disabledRoutes: [],

  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-ca-10006-jets-sec",
    mapId: "main",
  },

  checkoutCreditPackages: false,
  availableCreditEnabled: true,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    showFrontendTotalAmount: true,
    showFrontendServiceFee: true,
    allowManualCartInSimilar: false,
    isPerksEnabled: false,

    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: true,
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },
  isSaveCardAllowed: true,

  isBrainTreePaymentEnabled: false,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-QGN1EHSF4S",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "1900006853612117",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl:
      "https://flex.attendevents.com/winnipegjets_flex/events_home",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/tnse/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: AuthBackgroundBanner.src,
    checkoutStatusPageImage: AuthBackgroundBanner.src,
    giftCardStandalonePageImage: AuthBackgroundBanner.src,
    logoImage: LogoImage + "?width=84&height=66",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  colors: {
    primary: "#041E42",
  },

  howItWorks,
  faqs,

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideCheckoutCartBalance: true,
  },

  languages: {
    topNavMenuItems: <></>,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseGiftCard: purchaseGiftCardLanguages,
    redeemGiftCard: null,
    purchaseSeats: purchaseSeatsLanguages,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Tickets will be made available to manage in your Winnipeg Jets Account Manager after August 5th.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
